import { useCallback, useContext } from "react";
import { internalFetcher } from "core/fetcher";
import { usePromise } from "react-use";
import { CognitoAuthContext } from "components/Authentication/CognitoAuthContext";

/**
 * Provides a callback function which can be used to fetch
 * @param url the url to fetch from
 * @param fetcher callback function that should be used to do the actual fetch, should take in url and init parameters
 * @returns {{callbackFunction: (function(*=): Promise<unknown>)}}
 *
 * loading - true when post is currently happening.
 *
 * error - true when an error has occurred.
 *
 * callbackFunction - the callback function, takes a body object and returns  a response promise
 */
export function useFetch(url, fetcher = internalFetcher) {
  const mounted = usePromise();
  const { getToken } = useContext(CognitoAuthContext);

  /* fetches using the url provided to the hook and the init object supplied as params.
   * Calls onCompleted when successful.
   * Calls onError on failure. */
  const callbackFunction = useCallback(
    init => {
      const fetchFromUrl = async () => {
        let response = undefined;
        try {
          response = await mounted(fetcher(url, init, getToken));
        } catch (e) {
          // We don't care about abort errors as they will happen any time we abort a request
          if (e instanceof DOMException && e.name === "AbortError") {
            return Promise.resolve(undefined);
          } else {
            throw e;
          }
        }

        if (!response.ok) {
          // if response was not ok then there is no response to return
          // advanced error handling should be done using onError
          return Promise.reject(response);
        }

        const isJson = response.headers
          .get("content-type")
          ?.includes("application/json");

        const responseJson =
          response.status !== 204 && isJson ? await response.json() : undefined;

        return Promise.resolve(responseJson);
      };
      return fetchFromUrl();
    },
    [fetcher, getToken, mounted, url]
  );
  return { callbackFunction, getToken };
}
